'use client';

import { useState, useRef } from "react";
import { TallerButtonLink } from "@/components/taller/button";
import { useTranslations } from "@/i18n/client";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";

gsap.registerPlugin(ScrollTrigger, useGSAP);

interface ExtraItem {
  name: string;
  description: string;
  price: string;
}

interface ExtraSection {
  title: string;
  subtitle: string;
  items: ExtraItem[];
}

export function ServiciosContent() {
  const { t, isLoading } = useTranslations();
  const sv = t('servicios');
  const [isAnnual, setIsAnnual] = useState(false);
  const [openSection, setOpenSection] = useState<number | null>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    // Kill existing triggers to avoid duplicates on re-render
    ScrollTrigger.getAll().forEach(t => t.kill());

    // Hero Entrance
    gsap.fromTo(".hero-content > *", 
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, stagger: 0.15, ease: "power3.out" }
    );

    // Cards Entrance
    gsap.fromTo(".pricing-card", 
      { y: 40, opacity: 0 },
      { 
        y: 0, 
        opacity: 1, 
        duration: 0.8, 
        stagger: 0.1, 
        ease: "back.out(1.4)",
        scrollTrigger: {
          trigger: ".pricing-grid",
          start: "top 80%",
          once: true
        }
      }
    );

    // Extras Section Entrance
    gsap.fromTo(".extras-header > *", 
      { y: 20, opacity: 0 },
      { 
        y: 0, 
        opacity: 1, 
        duration: 0.6, 
        stagger: 0.1, 
        scrollTrigger: {
          trigger: ".extras-section",
          start: "top 85%",
          once: true
        }
      }
    );

    gsap.fromTo(".accordion-item", 
      { x: -20, opacity: 0 },
      { 
        x: 0, 
        opacity: 1, 
        duration: 0.5, 
        stagger: 0.1, 
        scrollTrigger: {
          trigger: ".accordion-list",
          start: "top 85%",
          once: true
        }
      }
    );

    // FAQ Entrance
    gsap.fromTo(".faq-item", 
      { y: 20, opacity: 0 },
      { 
        y: 0, 
        opacity: 1, 
        duration: 0.6, 
        stagger: 0.15, 
        scrollTrigger: {
          trigger: ".faq-section",
          start: "top 85%",
          once: true
        }
      }
    );
  }, { scope: containerRef });

  // Mouse move effect for cards
  const onCardMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    gsap.to(card, {
      "--mouse-x": `${x}px`,
      "--mouse-y": `${y}px`,
      duration: 0.4,
      ease: "power2.out"
    });
  };

  if (isLoading) return null;

  return (
    <div ref={containerRef} className="overflow-x-hidden">
      {/* HERO */}
      <section className="hero-content max-w-[var(--container-max)] mx-auto px-6 md:px-14 pt-28 md:pt-[72px] pb-16 text-center">
        <h1 className="taller-page-title">
          {sv.hero.title}<span className="text-[color:var(--color-accent)]">{sv.hero.titleAccent}</span>.
        </h1>
        <p className="font-sans text-[18px] md:text-[24px] leading-[1.5] text-[color:var(--color-mute)] mt-6 max-w-[700px] mx-auto">
          {sv.hero.subtitle}
        </p>
        
        {/* Toggle Billing */}
        <div className="flex items-center justify-center gap-4 mt-14">
          <span className={`font-sans text-[15px] font-medium transition-colors ${!isAnnual ? 'text-[color:var(--color-ink)]' : 'text-[color:var(--color-mute)]'}`}>
            {sv.hero.billing.monthly}
          </span>
          <button 
            type="button"
            className="relative inline-flex h-7 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none bg-[color:var(--color-surface)] border-[color:var(--color-border)] hover:border-[color:var(--color-accent)]/5 group/toggle"
            onClick={() => setIsAnnual(!isAnnual)}
            aria-label="Alternar facturación"
          >
            <span
              aria-hidden="true"
              className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-[color:var(--color-accent)] shadow-[0_0_15px_var(--color-accent-glow)] ring-0 transition duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover/toggle:scale-110 ${isAnnual ? 'translate-x-7' : 'translate-x-0'}`}
            />
          </button>
          <span className={`font-sans text-[15px] font-medium transition-colors flex items-center gap-2 ${isAnnual ? 'text-[color:var(--color-ink)]' : 'text-[color:var(--color-mute)]'}`}>
            {sv.hero.billing.annual} <span className="text-[10px] bg-[color:var(--color-accent)] text-white px-2 py-0.5 rounded-full uppercase tracking-wider font-bold shadow-[0_0_10px_var(--color-accent-glow)]">{sv.hero.billing.save}</span>
          </span>
        </div>
      </section>

      {/* PRICING CARDS */}
      <section className="pricing-grid max-w-[var(--container-max)] mx-auto px-6 md:px-14 pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
          
          {/* SEMILLA */}
          <div 
            onMouseMove={onCardMouseMove}
            className="pricing-card bg-[color:var(--color-surface)] rounded-2xl border border-[color:var(--color-border)] hover:border-[color:var(--color-accent)]/30 transition-colors duration-300 flex flex-col relative group"
          >
            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[radial-gradient(400px_circle_at_var(--mouse-x)_var(--mouse-y),rgba(4,212,207,0.06),transparent_80%)]" />
            </div>
            
            <div className="p-8 md:p-10 flex flex-col flex-1 relative z-10">
              <div className="text-center mb-8">
                <div className="text-[color:var(--color-accent)] mb-4 group-hover:scale-110 transition-transform duration-500">
                  <svg className="w-10 h-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg>
                </div>
                <h3 className="font-sans font-medium text-[28px] text-[color:var(--color-ink)] m-0">{sv.plans.semilla.name}</h3>
                <p className="font-sans text-[15px] text-[color:var(--color-mute)] mt-3 h-[45px]">{sv.plans.semilla.desc}</p>
                <div className="mt-8">
                  <span className="font-sans font-bold text-[36px] text-[color:var(--color-accent)]">{sv.plans.semilla.price}</span>
                  <span className="block text-[13px] uppercase tracking-widest text-[color:var(--color-mute)] font-mono mt-2">{sv.plans.semilla.sub}</span>
                </div>
              </div>
              
              <ul className="list-none p-0 m-0 mb-10 flex-1 space-y-5">
                {(sv.plans.semilla.features as string[]).map((f, i) => (
                  <li key={i} className="flex gap-3 text-[15px] leading-[1.4] text-[color:var(--color-mute)] group-hover:text-[color:var(--color-ink)] transition-colors duration-300">
                    <span className="text-[color:var(--color-accent)] shrink-0 mt-0.5">•</span>
                    {f}
                  </li>
                ))}
              </ul>
              
              <TallerButtonLink variant="secondary" href="/contacto" className="w-full">
                {sv.plans.semilla.button}
              </TallerButtonLink>
            </div>
          </div>

          {/* BROTA */}
          <div 
            onMouseMove={onCardMouseMove}
            className="pricing-card bg-[color:var(--color-bg)] rounded-2xl border-[3px] border-[color:var(--color-accent)] shadow-[var(--shadow-glow-accent)] lg:scale-105 transform flex flex-col relative z-20 group"
          >
            <div className="absolute -top-[18px] left-1/2 -translate-x-1/2 bg-[color:var(--color-accent)] text-white px-5 py-1.5 rounded-full text-[12px] font-bold tracking-widest uppercase font-mono shadow-lg z-30 whitespace-nowrap">
              {sv.plans.brota.tag}
            </div>

            <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[radial-gradient(400px_circle_at_var(--mouse-x)_var(--mouse-y),rgba(4,212,207,0.1),transparent_80%)]" />
            </div>

            <div className="p-8 md:p-10 flex flex-col flex-1 relative z-10">
              <div className="text-center mb-8 mt-2">
                <div className="text-[color:var(--color-accent)] mb-4 group-hover:scale-110 transition-transform duration-500">
                  <svg className="w-10 h-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" /></svg>
                </div>
                <h3 className="font-sans font-medium text-[28px] text-[color:var(--color-ink)] m-0">{sv.plans.brota.name}</h3>
                <p className="font-sans text-[15px] text-[color:var(--color-mute)] mt-3 h-[45px]">{sv.plans.brota.desc}</p>
                <div className="mt-8 flex flex-col items-center justify-center">
                  <div className="flex items-end gap-1">
                    <span className="font-sans font-bold text-[56px] leading-none text-[color:var(--color-accent)]">
                      {isAnnual ? sv.plans.brota.priceAnnual : sv.plans.brota.priceMonthly}
                    </span>
                  </div>
                  <span className="block text-[13px] uppercase tracking-widest text-[color:var(--color-mute)] font-mono mt-3">
                    {isAnnual ? sv.plans.brota.subAnnual : sv.plans.brota.subMonthly}
                  </span>
                </div>
              </div>
              
              <ul className="list-none p-0 m-0 mb-10 flex-1 space-y-5">
                {(sv.plans.brota.features as string[]).map((f, i) => (
                  <li key={i} className="flex gap-3 text-[15px] leading-[1.4] text-[color:var(--color-ink)] font-medium">
                    <span className="text-[color:var(--color-accent)] shrink-0 mt-0.5">✓</span>
                    {f}
                  </li>
                ))}
              </ul>
              
              <TallerButtonLink variant="primary" href="/contacto" className="w-full shadow-lg hover:shadow-xl transition-all duration-300 !py-4 text-[16px] group-hover:scale-[1.02]">
                {sv.plans.brota.button}
              </TallerButtonLink>
            </div>
          </div>

          {/* PERSONALIZADO */}
          <div 
            onMouseMove={onCardMouseMove}
            className="pricing-card bg-[color:var(--color-surface)] rounded-2xl border border-[color:var(--color-border)] hover:border-[color:var(--color-accent)]/30 transition-colors duration-300 flex flex-col relative group"
          >
             <div className="absolute inset-0 rounded-2xl overflow-hidden pointer-events-none">
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[radial-gradient(400px_circle_at_var(--mouse-x)_var(--mouse-y),rgba(4,212,207,0.06),transparent_80%)]" />
            </div>

            <div className="p-8 md:p-10 flex flex-col flex-1 relative z-10">
              <div className="text-center mb-8">
                <div className="text-[color:var(--color-accent)] mb-4 group-hover:scale-110 transition-transform duration-500">
                  <svg className="w-10 h-10 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>
                </div>
                <h3 className="font-sans font-medium text-[28px] text-[color:var(--color-ink)] m-0">{sv.plans.personalizado.name}</h3>
                <p className="font-sans text-[15px] text-[color:var(--color-mute)] mt-3 h-[45px]">{sv.plans.personalizado.desc}</p>
                <div className="mt-8">
                  <span className="font-sans font-bold text-[36px] text-[color:var(--color-accent)]">{sv.plans.personalizado.price}</span>
                  <span className="block text-[13px] uppercase tracking-widest text-[color:var(--color-mute)] font-mono mt-2">{sv.plans.personalizado.sub}</span>
                </div>
              </div>
              
              <div className="flex-1 flex items-center justify-center mb-10">
                <p className="font-sans text-[16px] leading-[1.6] text-[color:var(--color-ink)] text-center italic font-medium px-4">
                  {sv.plans.personalizado.quote}
                </p>
              </div>
              
              <TallerButtonLink variant="secondary" href="/contacto" className="w-full">
                {sv.plans.personalizado.button}
              </TallerButtonLink>
            </div>
          </div>

        </div>
      </section>

      {/* EXTRAS BROTA */}
      <section className="extras-section max-w-[var(--container-max)] mx-auto px-6 md:px-14 py-24 border-t border-[color:var(--color-border)]">
        <div className="extras-header mb-16">
          <h2 className="font-sans font-medium text-[32px] text-[color:var(--color-ink)] text-center mb-4">{sv.extras.title}</h2>
          <p className="font-sans text-[16px] text-[color:var(--color-mute)] text-center max-w-[600px] mx-auto">{sv.extras.subtitle}</p>
        </div>

        <div className="accordion-list space-y-3">
          {(sv.extras.sections as ExtraSection[]).map((section, sectionIdx) => (
            <AccordionItem 
              key={sectionIdx}
              section={section}
              isOpen={openSection === sectionIdx}
              onClick={() => setOpenSection(openSection === sectionIdx ? null : sectionIdx)}
              t_table={sv.extras.table as { extra: string, description: string, cost: string }}
            />
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="font-sans text-[15px] text-[color:var(--color-mute)] mb-6">{sv.extras.custom.text}</p>
          <TallerButtonLink variant="secondary" href="/contacto">
            {sv.extras.custom.button}
          </TallerButtonLink>
        </div>
      </section>
      
      {/* FAQ MINIMAL */}
      <section className="faq-section bg-[color:var(--color-surface)] py-24 border-t border-[color:var(--color-border)]">
        <div className="max-w-[800px] mx-auto px-6 text-center">
          <h2 className="font-sans font-medium text-[32px] text-[color:var(--color-ink)] mb-12">{sv.faq.title}</h2>
          <div className="grid gap-8 text-left">
            {(sv.faq.items as Array<{ q: string, a: string }>).map((faq, i) => (
              <div key={i} className="faq-item bg-[color:var(--color-bg)] p-6 md:p-8 rounded-xl border border-[color:var(--color-border)] hover:border-[color:var(--color-accent)]/30 transition-all duration-300 group/faq">
                <h4 className="font-sans font-bold text-[18px] text-[color:var(--color-ink)] mb-3 group-hover/faq:text-[color:var(--color-accent)] transition-colors">{faq.q}</h4>
                <p className="font-sans text-[15px] leading-[1.6] text-[color:var(--color-mute)] m-0">{faq.a}</p>
              </div>
            ))}
          </div>
          <div className="mt-16">
            <TallerButtonLink variant="primary" href="/contacto" size="lg">
              {sv.faq.cta}
            </TallerButtonLink>
          </div>
        </div>
      </section>
    </div>
  );
}

function AccordionItem({ section, isOpen, onClick, t_table }: { 
  section: ExtraSection, 
  isOpen: boolean, 
  onClick: () => void, 
  t_table: { extra: string, description: string, cost: string } 
}) {
  const contentRef = useRef<HTMLDivElement>(null);
  const internalRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    // Kill any existing animations on this ref to avoid conflicts
    gsap.killTweensOf(contentRef.current);
    
    // Simple robust height animation
    gsap.to(contentRef.current, {
      height: isOpen ? "auto" : 0,
      duration: 0.5,
      ease: "power3.inOut",
      opacity: isOpen ? 1 : 0,
      onStart: () => {
        if (isOpen) {
          gsap.fromTo(internalRef.current?.querySelectorAll(".extra-row") || [], 
            { y: 10, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.4, stagger: 0.05, ease: "power2.out", delay: 0.1 }
          );
        }
      }
    });
  }, { dependencies: [isOpen] });

  return (
    <div className="accordion-item border border-[color:var(--color-border)] rounded-xl overflow-hidden bg-[color:var(--color-surface)]/40 hover:bg-[color:var(--color-surface)]/60 transition-colors duration-300">
      {/* HEADER ACORDEÓN */}
      <button
        onClick={onClick}
        className="w-full px-8 py-6 md:py-8 flex items-center justify-between transition-all duration-200 text-left cursor-pointer group"
      >
        <div>
          <h3 className={`font-sans font-bold text-[20px] mb-1 transition-colors duration-300 ${isOpen ? 'text-[color:var(--color-accent)]' : 'text-[color:var(--color-ink)] group-hover:text-[color:var(--color-accent)]'}`}>
            {section.title}
          </h3>
          <p className="font-sans text-[14px] text-[color:var(--color-mute)]">{section.subtitle}</p>
        </div>
        <div className={`p-2 rounded-full border border-[color:var(--color-border)] transition-all duration-500 ${isOpen ? 'bg-[color:var(--color-accent)] border-[color:var(--color-accent)] rotate-180' : 'bg-transparent group-hover:border-[color:var(--color-accent)]'}`}>
          <svg
            className={`w-5 h-5 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-[color:var(--color-accent)]'}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* CONTENIDO ACORDEÓN */}
      <div 
        ref={contentRef} 
        className="overflow-hidden bg-gradient-to-b from-[color:var(--color-bg)]/30 to-transparent h-0 opacity-0"
      >
        <div ref={internalRef} className="px-8 py-8 border-t border-[color:var(--color-border)]">
          <div className="overflow-x-auto overflow-y-hidden [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[color:var(--color-border)]">
                  <th className="text-left py-4 px-4 font-sans font-bold text-[13px] uppercase tracking-wider text-[color:var(--color-mute)]">{t_table.extra}</th>
                  <th className="text-left py-4 px-4 font-sans font-bold text-[13px] uppercase tracking-wider text-[color:var(--color-mute)]">{t_table.description}</th>
                  <th className="text-right py-4 px-4 font-sans font-bold text-[13px] uppercase tracking-wider text-[color:var(--color-mute)]">{t_table.cost}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--color-border)]">
                {section.items.map((item, idx: number) => (
                  <tr key={idx} className="extra-row group/row hover:bg-[color:var(--color-accent)]/[0.03] transition-colors opacity-0">
                    <td className="py-5 px-4">
                      <p className="font-sans font-semibold text-[15px] text-[color:var(--color-ink)] m-0 group-hover/row:text-[color:var(--color-accent)] transition-colors">{item.name}</p>
                    </td>
                    <td className="py-5 px-4">
                      <p className="font-sans text-[14px] text-[color:var(--color-mute)] m-0">{item.description}</p>
                    </td>
                    <td className="py-5 px-4 text-right">
                      <span className="font-sans font-bold text-[15px] text-[color:var(--color-accent)] bg-[color:var(--color-accent)]/10 px-3 py-1 rounded-full">{item.price}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
